/**
 * Analytics Service
 * Core service for fetching and processing analytics data
 * Provides abstraction layer for switching between mock and Bedrock data sources
 */

import type {
  AnalyticsFilters,
  AnalyticsMetrics,
  SentimentAnalysis,
  IntentAnalysis,
  SessionDetails,
  AgentComparison,
  VolumeData,
} from '../types/analytics';

/**
 * Analytics Service Interface
 * Defines the contract for analytics data providers
 */
export interface IAnalyticsService {
  /**
   * Fetch aggregated metrics for the given filters
   */
  getMetrics(filters: AnalyticsFilters): Promise<AnalyticsMetrics>;

  /**
   * Fetch sentiment analysis data
   */
  getSentimentAnalysis(filters: AnalyticsFilters): Promise<SentimentAnalysis>;

  /**
   * Fetch intent analysis data
   */
  getIntentAnalysis(filters: AnalyticsFilters): Promise<IntentAnalysis>;

  /**
   * Fetch detailed session information
   */
  getSessionDetails(sessionId: string): Promise<SessionDetails>;

  /**
   * Fetch agent comparison data
   */
  getAgentComparison(filters: AnalyticsFilters): Promise<AgentComparison>;

  /**
   * Fetch conversation volume data
   */
  getConversationVolume(filters: AnalyticsFilters): Promise<VolumeData>;

  /**
   * Export analytics data to CSV format
   */
  exportData(filters: AnalyticsFilters): Promise<Blob>;

  /**
   * Fetch recent sessions for display in session list
   */
  getRecentSessions(filters: AnalyticsFilters, limit?: number): Promise<SessionDetails[]>;
}

/**
 * Analytics Service Implementation
 * Delegates to the configured data provider (Mock or Bedrock)
 */
class AnalyticsService implements IAnalyticsService {
  private dataProvider: IAnalyticsDataProvider;

  constructor(dataProvider: IAnalyticsDataProvider) {
    this.dataProvider = dataProvider;
  }

  /**
   * Set the data provider (allows switching between mock and Bedrock)
   */
  setDataProvider(provider: IAnalyticsDataProvider): void {
    this.dataProvider = provider;
  }

  async getMetrics(filters: AnalyticsFilters): Promise<AnalyticsMetrics> {
    return this.dataProvider.getMetrics(filters);
  }

  async getSentimentAnalysis(filters: AnalyticsFilters): Promise<SentimentAnalysis> {
    return this.dataProvider.getSentimentAnalysis(filters);
  }

  async getIntentAnalysis(filters: AnalyticsFilters): Promise<IntentAnalysis> {
    return this.dataProvider.getIntentAnalysis(filters);
  }

  async getSessionDetails(sessionId: string): Promise<SessionDetails> {
    return this.dataProvider.getSessionDetails(sessionId);
  }

  async getAgentComparison(filters: AnalyticsFilters): Promise<AgentComparison> {
    return this.dataProvider.getAgentComparison(filters);
  }

  async getConversationVolume(filters: AnalyticsFilters): Promise<VolumeData> {
    return this.dataProvider.getConversationVolume(filters);
  }

  async exportData(filters: AnalyticsFilters): Promise<Blob> {
    return this.dataProvider.exportData(filters);
  }

  async getRecentSessions(filters: AnalyticsFilters, limit?: number): Promise<SessionDetails[]> {
    return this.dataProvider.getRecentSessions(filters, limit);
  }
}

/**
 * Data Provider Interface
 * Contract that both Mock and Bedrock providers must implement
 */
export interface IAnalyticsDataProvider {
  getMetrics(filters: AnalyticsFilters): Promise<AnalyticsMetrics>;
  getSentimentAnalysis(filters: AnalyticsFilters): Promise<SentimentAnalysis>;
  getIntentAnalysis(filters: AnalyticsFilters): Promise<IntentAnalysis>;
  getSessionDetails(sessionId: string): Promise<SessionDetails>;
  getAgentComparison(filters: AnalyticsFilters): Promise<AgentComparison>;
  getConversationVolume(filters: AnalyticsFilters): Promise<VolumeData>;
  exportData(filters: AnalyticsFilters): Promise<Blob>;
  getRecentSessions(filters: AnalyticsFilters, limit?: number): Promise<SessionDetails[]>;
}

// Singleton instance
let analyticsServiceInstance: AnalyticsService | null = null;

/**
 * Initialize the analytics service with a data provider
 */
export function initializeAnalyticsService(provider: IAnalyticsDataProvider): AnalyticsService {
  analyticsServiceInstance = new AnalyticsService(provider);
  return analyticsServiceInstance;
}

/**
 * Get the analytics service instance
 * Throws error if not initialized
 */
export function getAnalyticsService(): AnalyticsService {
  if (!analyticsServiceInstance) {
    throw new Error('Analytics service not initialized. Call initializeAnalyticsService first.');
  }
  return analyticsServiceInstance;
}

export default AnalyticsService;
